import * as React from 'react'
import { cn } from '../lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './dropdown-menu'

/**
 * The app's dropdown menu: one behaviour, any trigger.
 *
 * There is exactly ONE menu implementation in this package — `dropdown-menu.tsx`, which wraps
 * Radix and adds the bits we care about (Tab escapes the menu back into the page instead of
 * being trapped; the focus indicator is an accent bar, because the tinted row background alone
 * is ~1.07:1 against the panel). `Menu` is the composed preset over it, and everything else —
 * `KebabMenu`, and any future menu — is `Menu` with a different trigger.
 *
 * That layering is the whole point: a menu's open/close, arrow-key navigation, type-ahead,
 * Escape, focus return, and `role="menu"` semantics are NOT re-specified per variant, so they
 * cannot drift between them. A variant owns only how its trigger and rows LOOK. If you find
 * yourself hand-rolling a dropdown — a `useState` plus an outside-click listener plus an
 * absolutely-positioned panel — reach for this instead; that shape silently loses all of the
 * above.
 *
 * Items stay composed (children) rather than passed as an array, because real menus here run
 * from two entries to half a dozen with separators and conditional branches. `{cond && <MenuItem/>}`
 * handles that; an items array would need to grow its own vocabulary for all of it.
 */
export interface MenuProps {
  /**
   * The trigger. Use **`MenuTrigger`** — it is a bare `<button>` you style however you like, and
   * it forwards what the menu needs.
   *
   * It is rendered through Radix's `asChild`, which CLONES this element to attach `onClick`,
   * `aria-haspopup`, `aria-expanded` and a ref. So it must be an element that forwards props and
   * refs to a real DOM node: `MenuTrigger`, or a plain `<button>`. A custom function component
   * that doesn't forward them silently swallows the lot and the menu simply never opens — no
   * error, no trigger attributes. (This bit the first story written against this API.)
   *
   * The trigger owns its own accessible name: visible text is enough, and an icon-only trigger
   * needs an `aria-label` naming the MENU, not any one item in it ("Plan options", not "End plan
   * early"). Deliberately not injected here — overriding a trigger that already has visible text
   * would break SC 2.5.3 (Label in Name).
   */
  trigger: React.ReactElement
  /** `MenuItem`s, `MenuSeparator`s, `MenuLabel`s, and whatever conditionals you need between them. */
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
  /**
   * Start open, uncontrolled — Radix owns open/close from there. In the app a menu opens from its
   * trigger, so this is for the case where the panel has to be on screen without an interaction:
   * a story, a screenshot, a doc page. Same prop, same reason, as `GooeyPopover`.
   */
  defaultOpen?: boolean
  /**
   * Set when the menu sits inside a clickable surface (a row that expands, a card that opens) —
   * clicks on the trigger and the panel then stay put instead of also firing the surface behind
   * them.
   */
  stopPropagation?: boolean
  /** Passed to the panel — e.g. to redirect focus somewhere other than the trigger on close. */
  onCloseAutoFocus?: (event: Event) => void
  /** Extra classes for the menu panel. */
  contentClassName?: string
}

export function Menu({
  trigger,
  children,
  align = 'end',
  stopPropagation = false,
  onCloseAutoFocus,
  contentClassName,
  defaultOpen
}: MenuProps) {
  const stop = stopPropagation ? (event: React.MouseEvent) => event.stopPropagation() : undefined

  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild onClick={stop}>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        onClick={stop}
        onCloseAutoFocus={onCloseAutoFocus}
        className={cn('min-w-[190px]', contentClassName)}>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The trigger element: a bare `<button type="button">` that forwards its ref and spreads props,
 * which is exactly what Radix's `asChild` needs. Bring your own classes — this adds no styling,
 * it exists so the correct wiring is the default rather than something each call site has to
 * remember. Pass an `aria-label` when the trigger is icon-only.
 */
const MenuTrigger = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(({ type, ...props }, ref) => (
  <button ref={ref} type={type ?? 'button'} {...props} />
))
MenuTrigger.displayName = 'MenuTrigger'

export interface MenuItemProps extends Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuItem>, 'children'> {
  children: React.ReactNode
  /** Leading icon, sized to match the label. */
  icon?: React.ReactNode
  /**
   * `destructive` tints the row rose, for deletes and other actions the user can't take back.
   * It was spelled three different ways across call sites before this existed.
   */
  tone?: 'default' | 'destructive'
}

const MenuItem = React.forwardRef<React.ElementRef<typeof DropdownMenuItem>, MenuItemProps>(
  ({ children, icon, tone = 'default', className, asChild = false, ...props }, ref) => {
    const classes = cn('cursor-pointer', tone === 'destructive' && 'text-red focus:bg-red-wash focus:text-red', className)

    // asChild: clone the single child (e.g. a Link) and inject the composed content as ITS
    // children, so Slot only ever receives one element. Rendering `{icon}{children}` here
    // instead hands Slot an ARRAY, and `React.Children.only` throws on render — even when
    // `icon` is undefined, because it's still a two-element array. Same approach, and the
    // same reason, as PillButton.
    if (asChild && React.isValidElement(children)) {
      return (
        <DropdownMenuItem ref={ref} asChild className={classes} {...props}>
          {React.cloneElement(
            children,
            undefined,
            <>
              {icon}
              {children.props.children}
            </>
          )}
        </DropdownMenuItem>
      )
    }

    return (
      <DropdownMenuItem ref={ref} className={classes} {...props}>
        {icon}
        {children}
      </DropdownMenuItem>
    )
  }
)
MenuItem.displayName = 'MenuItem'

const MenuSeparator = DropdownMenuSeparator
/** A non-actionable row — use for the current selection or a section heading, not for anything clickable. */
const MenuLabel = DropdownMenuLabel

export { MenuItem, MenuSeparator, MenuLabel, MenuTrigger }
